import { useState, useCallback } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import { Menu, Search, Package, KeyRound, Trash2 } from "lucide-react";
import useAuthStore from "../store/authStore";
import useNotificationStore from "../store/notificationStore";
import useWebSocket from "../hooks/useWebSocket";
import NotificationBell from "./NotificationBell";
import ApprovalNotificationBell from "./admin/approvals/ApprovalNotificationBell";
import AdminBellBadge from "./admin/AdminBellBadge";
import AgentGPSTracker from "./AgentGPSTracker";
import ChangePasswordDialog from "./ChangePasswordDialog";
import DeleteAccountDialog from "./DeleteAccountDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";

export default function DashboardLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const { addNotification } = useNotificationStore();
  const navigate = useNavigate();

  const onWsMessage = useCallback((data) => {
    if (data.type === "new_order") {
      addNotification({ type: "new_order", message: data.message || `New pickup: ${data.package_description}`, tracking_id: data.tracking_id, data });
      toast.info(`New pickup request: ${data.package_description}`, { duration: 5000 });
    } else if (data.type === "order_update") {
      addNotification({ type: "order_update", message: data.message, tracking_id: data.tracking_id });
      toast.info(data.message, { duration: 4000 });
    } else if (data.type === "hub_order_event") {
      // Sprint 71 — fan-out from the per-hub broadcaster. Surface a light
      // toast and emit a global window event so any hub dashboard mounted
      // can refetch without opening a 2nd WS connection.
      toast.info(data.message || `Order ${data.tracking_id} → ${data.status}`, { duration: 3500 });
      window.dispatchEvent(new CustomEvent("pickngo:hub_order_event", { detail: data }));
    }
  }, [addNotification]);

  const { sendLocation } = useWebSocket(onWsMessage);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50 " data-testid="dashboard-layout">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      {/* Mobile top bar */}
      <div className="md:hidden h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50 flex items-center justify-between px-4" data-testid="mobile-topbar">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
            data-testid="mobile-menu-toggle"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF6B00] rounded-lg flex items-center justify-center">
              <Package className="h-4 w-4 text-white" />
            </div>
            <span className="font-['Outfit'] text-lg font-extrabold tracking-tight"><span className="text-slate-900">Pick</span><span className="text-[#FF6B00]">N</span><span className="text-slate-900">Go</span></span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AgentGPSTracker sendLocation={sendLocation} />
          {["admin", "founder", "ceo"].includes((user?.role || "").toLowerCase()) && <AdminBellBadge />}
          <NotificationBell />
          <ApprovalNotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center" data-testid="mobile-user-menu">
                <span className="text-[#FF6B00] text-sm font-semibold">{(user?.name || "U")[0].toUpperCase()}</span>
              </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 rounded-xl">
            <DropdownMenuItem className="text-sm text-slate-600">{user?.email}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setChangePwdOpen(true)} className="text-sm cursor-pointer" data-testid="mobile-change-password-btn">
              <KeyRound className="h-4 w-4 mr-2" /> Change password
            </DropdownMenuItem>
            {user?.role !== "admin" && (
              <DropdownMenuItem onClick={() => setDeleteAccountOpen(true)} className="text-red-600 text-sm cursor-pointer" data-testid="mobile-delete-account-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Delete account
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={handleLogout} className="text-red-600 text-sm" data-testid="mobile-logout-btn">
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/30 z-40"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-sidebar-backdrop"
          />
          <div
            className="md:hidden fixed top-0 left-0 w-64 h-full bg-white z-50 shadow-2xl overflow-y-auto"
            data-testid="mobile-sidebar-drawer"
          >
            <Sidebar
              mobile
              collapsed={false}
              onToggle={() => setMobileMenuOpen(false)}
              onNavigate={() => setMobileMenuOpen(false)}
            />
          </div>
        </>
      )}

      {/* Main content */}
      <main className={`transition-all duration-300 ${sidebarCollapsed ? "md:ml-[72px]" : "md:ml-64"} min-h-screen`}>
        {/* Desktop top bar */}
        <div className="hidden md:flex h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-30 items-center justify-between px-8" data-testid="desktop-topbar">
          <div className="flex items-center gap-3 bg-slate-100 rounded-xl px-4 py-2.5 w-80">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search orders, agents..."
              className="bg-transparent border-none outline-none text-sm text-slate-600 placeholder:text-slate-400 w-full"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-4">
            <AgentGPSTracker sendLocation={sendLocation} />
            <NotificationBell />
          <ApprovalNotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 hover:bg-slate-50 rounded-xl px-3 py-2 transition-all" data-testid="user-dropdown-trigger">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-[#FF6B00] text-sm font-semibold font-['Outfit']">{(user?.name || "U")[0].toUpperCase()}</span>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-slate-900">{user?.name || "User"}</p>
                    <p className="text-xs text-slate-400 capitalize">{user?.role || "user"}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-xl">
                <DropdownMenuItem className="text-sm text-slate-500">{user?.email}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setChangePwdOpen(true)} className="text-sm cursor-pointer" data-testid="desktop-change-password-btn">
                  <KeyRound className="h-4 w-4 mr-2" /> Change password
                </DropdownMenuItem>
                {user?.role !== "admin" && (
                  <DropdownMenuItem onClick={() => setDeleteAccountOpen(true)} className="text-red-600 text-sm cursor-pointer" data-testid="desktop-delete-account-btn">
                    <Trash2 className="h-4 w-4 mr-2" /> Delete account
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 text-sm cursor-pointer" data-testid="desktop-logout-btn">
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>

      <ChangePasswordDialog open={changePwdOpen} onOpenChange={setChangePwdOpen} />
      <DeleteAccountDialog open={deleteAccountOpen} onOpenChange={setDeleteAccountOpen} />
    </div>
  );
}
